import type {
  PriorityAntecipeBandDto,
  PriorityBandsDto,
  PriorityConfig,
  PriorityInCashBandsDto,
  PrioritySlaBand,
  PrioritySlaKind,
  PrioritySlaState,
  PriorityTab
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import {
  formatSlaCountdown,
  formatSlaHour,
  formatSlaStamp,
  priorityThresholdsFor,
  resolveLiveSla
} from './slaFormat'

export type PrioritySlaClockTone = 'ok' | 'info' | 'crit' | 'warn' | 'mute'

export interface PrioritySlaClockLine {
  band: PrioritySlaBand
  title: string
  state: PrioritySlaState
  clock: string
  description: string
  progress: string
  done: number
  total: number
  segments: { tone: PrioritySlaClockTone; count: number }[]
}

interface LiveBand {
  state: PrioritySlaState
  clock: string
}

/** Estado e countdown de um prazo de linha, no tique, com os limiares do tipo. */
const liveBand = (
  deadlineAt: string,
  serverState: PrioritySlaState,
  kind: PrioritySlaKind,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): LiveBand => {
  const live = resolveLiveSla(
    {
      kind,
      stage: 'SEND',
      deadlineAt,
      state: serverState,
      remainingSeconds: null,
      startedAt: null,
      completedAt: null
    },
    nowMs,
    priorityThresholdsFor(config, kind)
  )
  return {
    state: live.state,
    clock: live.remainingSeconds === null ? '—' : formatSlaCountdown(live.remainingSeconds)
  }
}

const vencem = (count: number): string => (count > 1 ? 'plantões vencem' : 'plantão vence')

const sendLine = (
  inCash: PriorityInCashBandsDto,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine => {
  const { send, audit } = inCash
  const hour = formatSlaHour(send.deadlineAt)
  const live = liveBand(send.deadlineAt, send.state, 'IN_CASH', nowMs, config)
  return {
    band: 'IN_CASH_SEND',
    title: `À vista · Envio até ${hour}`,
    ...live,
    description:
      live.state === 'OVERDUE'
        ? `${send.pending} ficaram fora do prazo das ${hour}.`
        : `${send.pending} ${vencem(send.pending)} nele (${send.notSent} não enviados, ${send.rejected} rejeitados).`,
    progress: `${send.done} de ${send.total} cumpridos (enviado ou confirmado)`,
    done: send.done,
    total: send.total,
    segments: [
      { tone: 'ok', count: audit.done },
      { tone: 'info', count: audit.awaitingAudit },
      { tone: 'crit', count: send.rejected },
      { tone: 'warn', count: send.notSent }
    ]
  }
}

const auditLine = (
  inCash: PriorityInCashBandsDto,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine => {
  const { audit } = inCash
  const hour = formatSlaHour(audit.deadlineAt)
  const live = liveBand(audit.deadlineAt, audit.state, 'IN_CASH', nowMs, config)
  return {
    band: 'IN_CASH_AUDIT',
    title: `À vista · Auditoria até ${hour}`,
    ...live,
    description:
      live.state === 'OVERDUE'
        ? `${audit.pending} ficaram fora do prazo das ${hour}.`
        : `${audit.pending} ${vencem(audit.pending)} nele (${audit.awaitingAudit} na auditoria, ${audit.notSentOrRejected} sem envio).`,
    progress: `${audit.done} de ${audit.total} cumpridos (aprovado)`,
    done: audit.done,
    total: audit.total,
    segments: [
      { tone: 'ok', count: audit.done },
      { tone: 'info', count: audit.awaitingAudit },
      { tone: 'mute', count: audit.notSentOrRejected }
    ]
  }
}

const antecipeDescription = (
  antecipe: PriorityAntecipeBandDto,
  nextDeadlineAt: string,
  live: LiveBand,
  hours: number,
  nowMs: number | null
): string => {
  if (live.state === 'OVERDUE') {
    // O "próximo" que estourou no tique ainda não está em `overdue` (servidor).
    const justMissed = antecipe.state === 'OVERDUE' ? 0 : antecipe.nextDeadlineCount
    return `${antecipe.overdue + justMissed} ficaram fora das ${hours} h.`
  }
  const late = antecipe.overdue
    ? `, ${antecipe.overdue} já ${antecipe.overdue > 1 ? 'estourados' : 'estourado'}`
    : ''
  const count = antecipe.nextDeadlineCount
  return `Próximo às ${formatSlaStamp(nextDeadlineAt, nowMs)}: ${count} ${vencem(count)} nele. ${antecipe.pending} pendentes${late}.`
}

const antecipeLine = (
  antecipe: PriorityAntecipeBandDto,
  nextDeadlineAt: string,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine => {
  const hours = config?.antecipeSlaHours ?? 12
  const live = liveBand(nextDeadlineAt, antecipe.state, 'ANTECIPE', nowMs, config)
  return {
    band: 'ANTECIPE',
    title: `Antecipe · próximo vencimento (${hours} h)`,
    ...live,
    description: antecipeDescription(antecipe, nextDeadlineAt, live, hours, nowMs),
    progress: `${antecipe.done} de ${antecipe.total} cumpridos (últimos ${config?.antecipeWindowDays ?? 30} dias)`,
    done: antecipe.done,
    total: antecipe.total,
    segments: [
      { tone: 'ok', count: antecipe.done },
      { tone: 'crit', count: antecipe.overdue },
      { tone: 'mute', count: Math.max(antecipe.pending - antecipe.overdue, 0) }
    ]
  }
}

const inCashLines = (
  inCash: PriorityInCashBandsDto | null,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine[] => {
  if (!inCash) return []
  const lines: PrioritySlaClockLine[] = []
  if (inCash.send.pending > 0) lines.push(sendLine(inCash, nowMs, config))
  if (inCash.audit.pending > 0) lines.push(auditLine(inCash, nowMs, config))
  return lines
}

const antecipeLines = (
  antecipe: PriorityAntecipeBandDto | null,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine[] => {
  if (!antecipe || antecipe.pending <= 0 || !antecipe.nextDeadlineAt) return []
  return [antecipeLine(antecipe, antecipe.nextDeadlineAt, nowMs, config)]
}

/**
 * Linhas do relógio (M3 + apontamento 1): a linha some quando `pending = 0`;
 * sem linhas o relógio não renderiza. Aba À vista: só as duas do À vista; aba
 * Antecipe: só a do Antecipe. `bands = null` (fora do Ciclo atual): nada.
 */
export const buildPrioritySlaClockLines = (
  bands: PriorityBandsDto | null | undefined,
  tab: PriorityTab,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaClockLine[] => {
  if (!bands) return []
  return [
    ...(tab === 'ANTECIPE' ? [] : inCashLines(bands.inCash, nowMs, config)),
    ...(tab === 'IN_CASH' ? [] : antecipeLines(bands.antecipe, nowMs, config))
  ]
}
